import React, { forwardRef, useMemo, useState } from "react";
import DatePicker from "react-datepicker";
import { registerLocale } from "react-datepicker";
import ptBR from "date-fns/locale/pt-BR";
import moment from "moment";
import {
  fieldContainer,
  fieldControlBase,
  fieldControlError,
  fieldErrorText,
  fieldHelperText,
  fieldLabel,
} from "./inputTheme";
import "moment/locale/pt-br"; // traduz Moment

// 🔹 Configura Moment para PT-BR
moment.locale("pt-br");

// 🔹 Registra locale do calendário
registerLocale("pt-BR", ptBR);

/**
 * Props principais:
 * - label: string
 * - startDate, endDate: Date | null
 * - onChange: ( [start, end] ) => void
 * - nameStart, nameEnd: nomes para os inputs hidden (RHF)
 * - register: register do react-hook-form (opcional)
 * - required, disabled, readOnly, className, error
 * - dateFormat: default "dd/MM/yyyy"
 */
const DateRangeInput = ({
  className = "",
  label,
  startDate,
  endDate,
  onChange,
  nameStart = "startDate",
  nameEnd = "endDate",
  register, // opcional (react-hook-form)
  required = false,
  disabled = false,
  readOnly = false,
  error,
  dateFormat = "dd/MM/yyyy",
  ...props
}) => {
  const CustomInput = useMemo(
    () =>
      forwardRef(({ value, onClick }, ref) => (
        <input
          ref={ref}
          onClick={onClick}
          readOnly
          disabled={disabled}
          value={value || ""}
          placeholder="Todas as datas"
          className={`${fieldControlBase} ${error ? fieldControlError : ""} ${
            disabled ? "opacity-70" : "cursor-pointer"
          } ${className}`}
        />
      )),
    [className, disabled, error],
  );

  // valores formatados para os hidden inputs (RHF)
  const startISO = startDate ? moment(startDate).format("YYYY-MM-DD") : "";
  const endISO = endDate ? moment(endDate).format("YYYY-MM-DD") : "";

  return (
    <div className={fieldContainer}>
      {label && (
        <label className={fieldLabel}>
          {label}
          {required ? " *" : ""}
        </label>
      )}

      <DatePicker
        selectsRange
        startDate={startDate}
        endDate={endDate}
        onChange={(dates) => onChange?.(dates)}
        isClearable
        disabled={disabled}
        customInput={<CustomInput />}
        dateFormat={dateFormat}
        placeholderText="Todas as datas"
        locale="pt-BR"
        {...props}
      />

      {/* Integração com react-hook-form (opcional) */}
      {register && (
        <>
          <input
            type="hidden"
            {...register(nameStart, { required: false })}
            value={startISO}
            readOnly
          />
          <input
            type="hidden"
            {...register(nameEnd, { required: false })}
            value={endISO}
            readOnly
          />
        </>
      )}

      {error && <small className={fieldErrorText}>{error}</small>}
    </div>
  );
};

export default DateRangeInput;
